import './OnboardingSurvey.scss';
import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { completeSurvey, shuffleAnswers } from './helpers';
import { useNavigate } from 'react-router-dom';
import { analyticsTrack } from '../helpers/AnalyticsUtility';
import { saveEventToGoFormz } from '../helpers/GoFormzAnalytics';
import { SurveyAnswer, survey } from './data';
import CSSTransition from 'react-transition-group/CSSTransition';
import { Features, canIUse } from '../helpers/PermissionUtility';
import { Progress } from 'semantic-ui-react';
import { useUserPersistedState } from '../persistedstate/users/useUserPersistedState';

export default function OnboardingSurvey() {
  const navigate = useNavigate();
  const questionRef = useRef(null);
  const [questionNumber, setQuestionNumber] = useState(1);
  const [currentAnswer, setCurrentAnswer] = useState<string>();
  const [answers, setAnswers] = useState<SurveyAnswer[]>([]);
  const [fade, setFade] = useState(false);
  const currentQuestion = survey[questionNumber];
  const [progress, setProgress] = useState(0);
  const [firstTemplateStarted, setFirstTemplateStarted] = useUserPersistedState('firstTemplateStarted');
  const [firstTemplatePublished, setFirstTemplatePublished] = useUserPersistedState('firstTemplatePublished');

  useEffect(() => {
    setTimeout(() => {
      if (progress < 33 && questionNumber == 1) {
        setProgress(progress + 1);
      }
      if (progress < 66 && questionNumber == 3) {
        setProgress(progress + 1);
      }
      if (progress < 100 && questionNumber == 4) {
        setProgress(progress + 1);
      }
    }, currentQuestion.delay / 33);
  }, [progress, questionNumber]);

  useEffect(() => {
    // currently, the first and fourth questions don't have any anwsers, move on immediately
    if (questionNumber == 1) {
      handleSurveySelection(null, null);
    }

    if (currentQuestion.last) {
      setTimeout(() => {
        if (canIUse(Features.DisableOnboardingNavigation)) {
          navigate('/templates/new/selectview', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      }, currentQuestion.delay);

      completeSurvey();
    }
  }, [questionNumber]);

  useEffect(() => {
    if (!currentQuestion.answers) {
      return;
    }

    setAnswers(null);
    setAnswers(shuffleAnswers(currentQuestion.answers));
  }, [currentQuestion.answers]);

  async function handleSurveySelection(answer?: string, answerKey?: string) {
    // filter out multiple clicks
    if (!!currentAnswer) {
      return;
    }

    setCurrentAnswer(answer);
    setFade(false);

    // only need to track answers, if there is one
    if (!!currentQuestion.trackEvent) {
      analyticsTrack(currentQuestion.trackEvent, {
        [currentQuestion.trackAs]: answer
      });

      saveEventToGoFormz(currentQuestion.trackGoFormzEvent, answerKey);
    }

    // move to the next question
    setTimeout(() => {
      setCurrentAnswer(null);
      setFade(true);
      setQuestionNumber((q) => q + 1);
    }, currentQuestion.delay);
  }

  useEffect(() => {
    // if going through survey, reset the onboarding state
    if (firstTemplateStarted || firstTemplatePublished) {
      setFirstTemplateStarted(false);
      // wait 1 second before running the next request to ensure the updates are both run in proper order
      setTimeout(() => {
        setFirstTemplatePublished(false);
      }, 1000);
    }
  }, []);

  return (
    <div>
      <Progress className="progress-bar-old" percent={progress} size="tiny" color="blue" />
      <div className="survey-page-old">
        <CSSTransition className="fade" timeout={1000} classNames="fade" in={fade} nodeRef={questionRef}>
          <div className="survey-question-container" ref={questionRef}>
            <div className="survey-question">{currentQuestion.title}</div>
            <div className={classNames('survey-answers', currentQuestion.className)}>
              {answers.map((option) => (
                <div
                  role="checkbox"
                  key={option.key}
                  className={classNames('survey-answer', { checked: currentAnswer === option.answer })}
                  onClick={() => handleSurveySelection(option.answer, option.key)}
                >
                  <span className="checkmark"></span>
                  <label>{option.answer}</label>
                </div>
              ))}
            </div>
          </div>
        </CSSTransition>
      </div>
    </div>
  );
}
